/**
 * Shared reading-typography tokens for long-form HTML (markdown → HTML).
 * Web applies these as CSS variables via getReadingTypographyCssVars().
 * Native maps them via getReadingTypographyTagsStyles / getReadingTypographyBaseFontStyle.
 */

export const READING_TYPOGRAPHY = {
  fontFamily: "Archivo, sans-serif",
  color: "#000000",
  linkColor: "#180245",
  mutedColor: "#666666",
  codeBackground: "#f5f5f5",
  blockquoteBorderColor: "#d0d0d0",
  strongFontWeight: "700",
  body: {
    fontSize: 16,
    lineHeight: 1.65,
    fontWeight: "400",
    marginTop: 0,
    marginBottom: 12
  },
  h1: {
    fontSize: 24,
    lineHeight: 1.3,
    fontWeight: "600",
    marginTop: 20,
    marginBottom: 12
  },
  h2: {
    fontSize: 20,
    lineHeight: 1.35,
    fontWeight: "600",
    marginTop: 18,
    marginBottom: 10
  },
  h3: {
    fontSize: 18,
    lineHeight: 1.4,
    fontWeight: "600",
    marginTop: 16,
    marginBottom: 8
  },
  h4: {
    fontSize: 16,
    lineHeight: 1.45,
    fontWeight: "600",
    marginTop: 14,
    marginBottom: 8
  },
  h5: {
    fontSize: 16,
    lineHeight: 1.65,
    fontWeight: "600",
    marginTop: 12,
    marginBottom: 6,
    color: "#666666"
  },
  list: {
    paddingLeft: 20,
    paddingLeftMobile: 14,
    nestedPaddingLeft: 8,
    marginTop: 0,
    marginBottom: 12
  },
  listItem: {
    marginTop: 0,
    marginBottom: 6
  },
  blockquote: {
    fontSize: 16,
    lineHeight: 1.65,
    paddingLeft: 12,
    marginTop: 0,
    marginBottom: 12,
    borderLeftWidth: 3
  },
  code: {
    fontSize: 14,
    lineHeight: 1.5
  },
  pre: {
    fontSize: 14,
    lineHeight: 1.5,
    padding: 12,
    marginTop: 0,
    marginBottom: 12
  }
};

const px = value => `${value}px`;

const toNativeLineHeight = (fontSize, lineHeight) =>
  Math.round(fontSize * lineHeight);

export const getReadingTypographyBaseFontStyle = () => {
  const { body, color } = READING_TYPOGRAPHY;

  return {
    fontSize: body.fontSize,
    lineHeight: toNativeLineHeight(body.fontSize, body.lineHeight),
    color,
    fontWeight: body.fontWeight
  };
};

export const getReadingTypographyCssVars = () => {
  const {
    fontFamily,
    color,
    linkColor,
    mutedColor,
    codeBackground,
    blockquoteBorderColor,
    strongFontWeight,
    body,
    h1,
    h2,
    h3,
    h4,
    h5,
    list,
    listItem,
    blockquote,
    code,
    pre
  } = READING_TYPOGRAPHY;

  return {
    "--rt-font-family": fontFamily,
    "--rt-color": color,
    "--rt-link-color": linkColor,
    "--rt-muted-color": mutedColor,
    "--rt-code-bg": codeBackground,
    "--rt-blockquote-border-color": blockquoteBorderColor,
    "--rt-strong-weight": strongFontWeight,
    "--rt-body-size": px(body.fontSize),
    "--rt-body-line-height": String(body.lineHeight),
    "--rt-body-weight": body.fontWeight,
    "--rt-body-margin-top": px(body.marginTop),
    "--rt-body-margin-bottom": px(body.marginBottom),
    "--rt-h1-size": px(h1.fontSize),
    "--rt-h1-line-height": String(h1.lineHeight),
    "--rt-h1-weight": h1.fontWeight,
    "--rt-h1-margin-top": px(h1.marginTop),
    "--rt-h1-margin-bottom": px(h1.marginBottom),
    "--rt-h2-size": px(h2.fontSize),
    "--rt-h2-line-height": String(h2.lineHeight),
    "--rt-h2-weight": h2.fontWeight,
    "--rt-h2-margin-top": px(h2.marginTop),
    "--rt-h2-margin-bottom": px(h2.marginBottom),
    "--rt-h3-size": px(h3.fontSize),
    "--rt-h3-line-height": String(h3.lineHeight),
    "--rt-h3-weight": h3.fontWeight,
    "--rt-h3-margin-top": px(h3.marginTop),
    "--rt-h3-margin-bottom": px(h3.marginBottom),
    "--rt-h4-size": px(h4.fontSize),
    "--rt-h4-line-height": String(h4.lineHeight),
    "--rt-h4-weight": h4.fontWeight,
    "--rt-h4-margin-top": px(h4.marginTop),
    "--rt-h4-margin-bottom": px(h4.marginBottom),
    "--rt-h5-size": px(h5.fontSize),
    "--rt-h5-line-height": String(h5.lineHeight),
    "--rt-h5-weight": h5.fontWeight,
    "--rt-h5-color": h5.color,
    "--rt-h5-margin-top": px(h5.marginTop),
    "--rt-h5-margin-bottom": px(h5.marginBottom),
    "--rt-list-padding-left": px(list.paddingLeft),
    "--rt-list-padding-left-mobile": px(list.paddingLeftMobile),
    "--rt-nested-list-padding-left": px(list.nestedPaddingLeft),
    "--rt-list-margin-top": px(list.marginTop),
    "--rt-list-margin-bottom": px(list.marginBottom),
    "--rt-li-margin-top": px(listItem.marginTop),
    "--rt-li-margin-bottom": px(listItem.marginBottom),
    "--rt-blockquote-size": px(blockquote.fontSize),
    "--rt-blockquote-line-height": String(blockquote.lineHeight),
    "--rt-blockquote-padding-left": px(blockquote.paddingLeft),
    "--rt-blockquote-margin-top": px(blockquote.marginTop),
    "--rt-blockquote-margin-bottom": px(blockquote.marginBottom),
    "--rt-blockquote-border-width": px(blockquote.borderLeftWidth),
    "--rt-code-size": px(code.fontSize),
    "--rt-code-line-height": String(code.lineHeight),
    "--rt-pre-size": px(pre.fontSize),
    "--rt-pre-line-height": String(pre.lineHeight),
    "--rt-pre-padding": px(pre.padding),
    "--rt-pre-margin-top": px(pre.marginTop),
    "--rt-pre-margin-bottom": px(pre.marginBottom)
  };
};

export const getReadingTypographyTagsStyles = () => {
  const {
    body,
    h1,
    h2,
    h3,
    h4,
    h5,
    list,
    listItem,
    linkColor,
    mutedColor,
    codeBackground,
    blockquoteBorderColor,
    strongFontWeight,
    blockquote,
    code,
    pre,
    color
  } = READING_TYPOGRAPHY;

  return {
    p: {
      fontSize: body.fontSize,
      lineHeight: toNativeLineHeight(body.fontSize, body.lineHeight),
      fontWeight: body.fontWeight,
      color,
      marginTop: body.marginTop,
      marginBottom: body.marginBottom
    },
    h1: {
      fontSize: h1.fontSize,
      lineHeight: toNativeLineHeight(h1.fontSize, h1.lineHeight),
      fontWeight: h1.fontWeight,
      color,
      marginTop: h1.marginTop,
      marginBottom: h1.marginBottom
    },
    h2: {
      fontSize: h2.fontSize,
      lineHeight: toNativeLineHeight(h2.fontSize, h2.lineHeight),
      fontWeight: h2.fontWeight,
      color,
      marginTop: h2.marginTop,
      marginBottom: h2.marginBottom
    },
    h3: {
      fontSize: h3.fontSize,
      lineHeight: toNativeLineHeight(h3.fontSize, h3.lineHeight),
      fontWeight: h3.fontWeight,
      color,
      marginTop: h3.marginTop,
      marginBottom: h3.marginBottom
    },
    h4: {
      fontSize: h4.fontSize,
      lineHeight: toNativeLineHeight(h4.fontSize, h4.lineHeight),
      fontWeight: h4.fontWeight,
      color,
      marginTop: h4.marginTop,
      marginBottom: h4.marginBottom
    },
    h5: {
      fontSize: h5.fontSize,
      lineHeight: toNativeLineHeight(h5.fontSize, h5.lineHeight),
      fontWeight: h5.fontWeight,
      color: h5.color,
      marginTop: h5.marginTop,
      marginBottom: h5.marginBottom
    },
    ul: {
      paddingLeft: list.nestedPaddingLeft,
      marginLeft: 0,
      marginTop: list.marginTop,
      marginBottom: list.marginBottom
    },
    ol: {
      paddingLeft: list.paddingLeftMobile,
      marginLeft: 0,
      marginTop: list.marginTop,
      marginBottom: list.marginBottom
    },
    li: {
      fontSize: body.fontSize,
      lineHeight: toNativeLineHeight(body.fontSize, body.lineHeight),
      color,
      marginTop: listItem.marginTop,
      marginBottom: listItem.marginBottom
    },
    a: {
      color: linkColor,
      textDecorationLine: "underline"
    },
    strong: {
      fontWeight: strongFontWeight
    },
    b: {
      fontWeight: strongFontWeight
    },
    em: {
      fontStyle: "italic"
    },
    i: {
      fontStyle: "italic"
    },
    blockquote: {
      fontSize: blockquote.fontSize,
      lineHeight: toNativeLineHeight(
        blockquote.fontSize,
        blockquote.lineHeight
      ),
      color: mutedColor,
      paddingLeft: blockquote.paddingLeft,
      marginTop: blockquote.marginTop,
      marginBottom: blockquote.marginBottom,
      borderLeftWidth: blockquote.borderLeftWidth,
      borderLeftColor: blockquoteBorderColor
    },
    code: {
      fontSize: code.fontSize,
      lineHeight: toNativeLineHeight(code.fontSize, code.lineHeight),
      backgroundColor: codeBackground
    },
    pre: {
      fontSize: pre.fontSize,
      lineHeight: toNativeLineHeight(pre.fontSize, pre.lineHeight),
      backgroundColor: codeBackground,
      padding: pre.padding,
      marginTop: pre.marginTop,
      marginBottom: pre.marginBottom
    }
  };
};

export default READING_TYPOGRAPHY;
